import get from 'immutable-less/transmute/get';
import getIn from 'immutable-less/transmute/getIn';
import { List } from 'immutable';
import I18n from 'I18n';
import { COMPANY_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import LogEventKeys from 'automation-ui-log-events/constants/LogEventKeys';
import { LogEventTypes } from 'automation-ui-log-events/constants/LogEventConstants';
import { getAcceptedListId, getAcceptedBranchValue, getAcceptedBranchOperator, getAcceptedConnectionIndex } from 'automation-ui-log-events/records/LogEvent/contextMapUtils';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import Action from 'automation-ui-action-features/records/Action';
import { DEFAULT_BRANCH_INDEX } from 'automation-ui-action-features/records/recordsUtils';
export const getExecutionState = get(LogEventKeys.EXECUTION_STATE);
export const getErrorType = get(LogEventKeys.ERROR_TYPE);
export const getErrorSubType = get(LogEventKeys.ERROR_SUB_TYPE);
export const getFailType = get(LogEventKeys.FAIL_TYPE);
export const getFailSubType = get(LogEventKeys.FAIL_SUB_TYPE);
export const getSkipReason = get(LogEventKeys.SKIP_REASON);
export const getMessage = get(LogEventKeys.MESSAGE);
export const getAction = get(LogEventKeys.ACTION);
export const getNextActionId = get(LogEventKeys.NEXT_ACTION_ID);
export const getContextMap = get(LogEventKeys.CONTEXT_MAP);
export const getEnrollmentState = get(LogEventKeys.ENROLLMENT_STATE);
export const getEnrollType = get(LogEventKeys.ENROLL_TYPE);
export const getUnenrollType = get(LogEventKeys.UNENROLL_TYPE);
export const getType = get(LogEventKeys.TYPE);
export const getPortalId = get(LogEventKeys.PORTAL_ID);
export const getFlowId = get(LogEventKeys.FLOW_ID);
// Dev note - getObjectTypeId is not safe to use since Custom-Object based
// workflows do not have reliable objectTypeIds on their log events
export const getObjectTypeId = get(LogEventKeys.OBJECT_TYPE_ID);
export const getObjectId = get(LogEventKeys.OBJECT_ID);
export const getEnrollmentId = get(LogEventKeys.ENROLLMENT_ID);
export const getPreEnrollmentId = get(LogEventKeys.PRE_ENROLLMENT_ID);
export const getTimestamp = get(LogEventKeys.TIMESTAMP);
export const getId = get(LogEventKeys.ID);
export const getIdempotencyId = get(LogEventKeys.IDEMPOTENCY_ID);
export const getActionExecutionIndex = get(LogEventKeys.ACTION_EXECUTION_INDEX);
export const getFilterEventType = get(LogEventKeys.FILTER_EVENT_TYPE);
export const getTriggerEnrollType = get(LogEventKeys.TRIGGER_ENROLL_TYPE);
export const getNotificationType = get(LogEventKeys.NOTIFICATION_TYPE);
export const getActionType = getIn([LogEventKeys.ACTION, 'actionType']);
export const getActionConnection = getIn([LogEventKeys.ACTION, 'connection']);
export const getState = logEvent => {
  const type = getType(logEvent);
  switch (type) {
    case LogEventTypes.ENROLLMENT:
    case LogEventTypes.UNENROLLMENT:
      return getEnrollmentState(logEvent);
    case LogEventTypes.ACTION_EXECUTION:
      return getExecutionState(logEvent);
    default:
      return undefined;
  }
};

/**
 * For a branch log event, return the branchIndex that the
 * next action id points to.
 * For AB_TEST_BRANCH, it returns the accepted connection index from the context map.
 * For non-branch log events, it will always return null.
 *
 * For branches without ACCEPTED_LIST_ID data, it'll return DEFAULT_BRANCH_INDEX.
 * For branches with ACCEPTED_LIST_ID data, it'll return the
 * branch index, an integer greater than or equal to 0.
 * @param {LogEvent} logEvent
 */
export const getFollowedBranchIndex = logEvent => {
  var _getAcceptedConnectio;
  const action = new Action(getAction(logEvent));
  const connection = action.getConnection();
  if (!connection || !connection.isBranchConnection()) {
    return undefined;
  }
  let acceptedBranchIdentifier;
  let getAcceptedBranchIdentifier;
  const actionType = action.getActionType();
  switch (actionType) {
    case ActionTypes.LIST_BRANCH:
      acceptedBranchIdentifier = getAcceptedListId(logEvent);
      getAcceptedBranchIdentifier = branch => branch.getListId();
      break;
    case ActionTypes.STATIC_BRANCH:
      acceptedBranchIdentifier = `${getAcceptedBranchValue(logEvent)}::${getAcceptedBranchOperator(logEvent) || 'IS_EQUAL_TO'}`;
      // the unique identifier for a static branch is the
      // value + operator
      getAcceptedBranchIdentifier = branch => `${branch.value}::${branch.operator}`;
      break;
    case ActionTypes.AB_TEST_BRANCH:
      return Number((_getAcceptedConnectio = getAcceptedConnectionIndex(logEvent)) !== null && _getAcceptedConnectio !== void 0 ? _getAcceptedConnectio : DEFAULT_BRANCH_INDEX);
    default:
      break;
  }
  if (!acceptedBranchIdentifier) {
    return DEFAULT_BRANCH_INDEX;
  }
  const branches = connection.getBranches() || List();
  return branches.findIndex(branch => getAcceptedBranchIdentifier(branch) === acceptedBranchIdentifier);
};
export const getNextEdgeId = logEvent => {
  const action = getAction(logEvent);
  if (!action) {
    return undefined;
  }
  const actionId = action.getActionId();
  const followedBranchIndex = getFollowedBranchIndex(logEvent);
  if (!followedBranchIndex) {
    return undefined;
  }
  const edgeModifier = followedBranchIndex + 2;
  return `${actionId}-edge-${edgeModifier}`;
};
export const getNextActionIsGoTo = logEvent => {
  const actionObj = getAction(logEvent);
  if (!actionObj) {
    return false;
  }
  const action = new Action(actionObj);
  const connection = action.getConnection();
  if (!connection) {
    return false;
  }
  let connector;
  if (connection && connection.isBranchConnection()) {
    const branchConnection = connection;
    const followedBranchIndex = getFollowedBranchIndex(logEvent);
    connector = branchConnection.getConnectorByBranchIndex(followedBranchIndex);
  } else {
    connector = connection;
  }
  return connector && connector.isGoToEdge();
};
const getIsActionCreateCompanyRecord = action => {
  return action && action.metadata && action.getActionType() === ActionTypes.CREATE_OBJECT && action.metadata.objectTypeId === COMPANY_ID;
};
export const getPropertiesFromMessage = logEvent => {
  const message = getMessage(logEvent);
  const action = new Action(getAction(logEvent));
  try {
    if (!message) return undefined;
    // adding special case for UX Jira 4860
    if (getIsActionCreateCompanyRecord(action)) {
      return I18n.text('automation-ui-log-events.ACTION_EXECUTION.FAIL_CONTINUE.CREATE_OBJECT.COMPANY_PROPERTIES');
    }
    return message.match(/[^[\]]+(?=])/g).join(', ');
  } catch (e) {
    console.error('Error parsing properties from message: ', e);
    return message;
  }
};